import { Presentation, ImageWithMetadata } from '@features/powerpoint';
import { Campaign } from '@features/campaigns';
import { DEFAULT_FILENAME } from '@features/executive-report/utils';
import { createSharedPresentationConfig } from '@features/executive-report/utils/shared-presentation-config';
import { FormSchema } from '../form-schema';
import {
  visibleSlide,
  meetYourTeamSlide,
  agendaSlide,
  navigatingComplexitySlide,
  incrementalityMattersSlide,
  coverSlide,
  titleSlide,
  recommendationsSlide,
} from '@features/executive-report/slides';
import {
  campaignOverviewSlide,
  CampaignOverviewSlideOptions,
} from '../slides/campaign-overview/campaign-overview';
import { discoverWhatsNextSlide } from '@features/executive-report/slides/discover-whats-next';
import { campaignOverviewSlide2 } from '../slides/campaign-overview-2/campaign-overview-2';
import { SubdimensionCounts } from '@features/reports';
import { salesDimensionsSlides } from '../slides/sales-dimensions/sales-dimensions';
import { headerTabs } from '../slides/header-tabs';

export type BuildReportOptions = {
  brandLogo: ImageWithMetadata | null;
  campaign: Pick<Campaign, 'name' | 'startDate' | 'endDate' | 'adSpend'>;
  customFields: FormSchema;
  report: CampaignOverviewSlideOptions['report'];
  retailerCount: number;
  subdimensionCounts: SubdimensionCounts;
  roasData: {
    iroas: number | null;
    roas: number | null;
  };
  topSalesSubdimensionsByName: Parameters<
    typeof salesDimensionsSlides
  >[0]['subdimensionsByName'];
  topRetailers: Parameters<typeof salesDimensionsSlides>[0]['topRetailers'];
};

export const buildPresentation = (
  options: BuildReportOptions
): Presentation => {
  const basePresentation = createSharedPresentationConfig(options.brandLogo);

  return {
    ...basePresentation,
    filename: [options.campaign.name, DEFAULT_FILENAME].join(' - '),
    slides: [
      ...visibleSlide({
        slide: titleSlide(
          options.campaign,
          options.brandLogo,
          options.customFields.campaignName
        ),
        visible: options.customFields.includedSlides.intro.title,
      }),
      ...visibleSlide({
        slide: meetYourTeamSlide({
          teamMembers: options.customFields.teamMembers,
        }),
        visible: options.customFields.includedSlides.intro.meetYourTeam,
      }),
      ...visibleSlide({
        slide: agendaSlide([
          { title: 'The Who', text: ' – Audience' },
          { title: 'The What', text: ' – Creative' },
          { title: 'The Where', text: ' – Channels' },
          { title: 'The How', text: ' – Retailers where purchase happened' },
        ]),
        visible: options.customFields.includedSlides.intro.agenda,
      }),
      ...visibleSlide({
        slide: navigatingComplexitySlide(),
        visible: options.customFields.includedSlides.intro.navigatingComplexity,
      }),
      ...visibleSlide({
        slide: incrementalityMattersSlide({
          liftText: 'lift in item level sales',
        }),
        visible:
          options.customFields.includedSlides.intro.incrementalityMatters,
      }),
      ...visibleSlide({
        slide: coverSlide({ titleText: 'Sales Attribution' }),
        visible: options.customFields.includedSlides.sales.cover,
      }),
      ...visibleSlide({
        slide: campaignOverviewSlide({
          campaign: {
            ...options.campaign,
            adSpend: options.campaign.adSpend ?? 0,
          },
          report: options.report,
          brandLogoWithMetadata: options.brandLogo,
          dimensionCounts: [
            options.subdimensionCounts.aud ?? 0,
            options.subdimensionCounts.creative ?? 0,
            options.subdimensionCounts.channel ?? 0,
            options.retailerCount,
          ],
        }),
        visible: options.customFields.includedSlides.sales.campaignOverview,
      }),
      ...visibleSlide({
        slide: campaignOverviewSlide2({
          report: options.report,
          brandLogoWithMetadata: options.brandLogo,
          objectives: options.customFields.salesObjectives.map(
            objective => objective.value
          ),
          iroas: options.roasData.iroas,
        }),
        visible: options.customFields.includedSlides.sales.campaignOverview2,
      }),
      ...salesDimensionsSlides({
        topRetailers: options.topRetailers,
        salesMetrics: options.report,
        subdimensionsByName: options.topSalesSubdimensionsByName,
        recommendations: options.customFields.salesDimensionRecommendations,
        includedSlides: options.customFields.includedSlides.sales,
        subdimensionImages:
          options.customFields.subdimensionImagesByNameAndValue,
      }),
      ...visibleSlide({
        slide: recommendationsSlide(
          options.customFields.recommendations.map(r => r.value),
          headerTabs('recommendations')
        ),
        visible: options.customFields.includedSlides.takeaways.recommendations,
      }),
      ...visibleSlide({
        slide: discoverWhatsNextSlide({
          observations: options.customFields.observations.map(o => o.value),
        }),
        visible:
          options.customFields.includedSlides.takeaways.discoverWhatsNext,
      }),
    ],
  };
};
